import { createSelector } from '@reduxjs/toolkit';
import { useEffect, useMemo, useRef } from 'preact/hooks';

import { AppState, useSelector } from '@/redux/helper';
import { createSortedHabitatsSelector } from '@/redux/selectors/habitats';

const selectIsAllHabitatsOffline = createSelector(
  (state: AppState) => state.allHabitats,
  (habitats) => habitats.length > 0 && habitats.every(({ isStreamOn }) => !isStreamOn),
);

interface UseHabitatForStreamOptions {
  keepPreferredHabitatId: boolean;
}

export const useHabitatForStream = ({ keepPreferredHabitatId }: UseHabitatForStreamOptions) => {
  const preferredHabitatIdRef = useRef<string>();
  const selectHabitatForStream = useMemo(
    () =>
      createSelector(
        createSortedHabitatsSelector(['online', 'lastAnimalAlert', 'lastMediaCapture', 'index', 'currentViewers', 'title']),
        (_: AppState, habitatId: string | undefined) => habitatId,
        (habitats, habitatId) => {
          const habitat = habitatId ? habitats.find((habitat) => habitat._id === habitatId) : undefined;
          if (habitat && habitat.isStreamOn) {
            return habitat;
          }
          return habitats.find(({ isStreamOn }) => isStreamOn);
        },
      ),
    [],
  );

  const habitat = useSelector((state) =>
    selectHabitatForStream(state, keepPreferredHabitatId ? preferredHabitatIdRef.current : undefined),
  );

  useEffect(() => {
    preferredHabitatIdRef.current = habitat?._id;
  }, [habitat?._id]);

  return habitat;
};

export const useIsAllHabitatsOffline = () => useSelector(selectIsAllHabitatsOffline);
